<script setup lang="ts">

import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaLabel from "@/components/NamaLabel.vue";
import {QUploader} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import {computed, ref, Ref, watch} from "vue";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import {ChatAttachment} from "@/utils/presenters/interfaces.ts";
import FileUploaderWidgetUtil from "@/utils/editView/widgets/FileUploaderWidgetUtil.ts";
import NamaChatVoice from "@/components/navBar/chat/NamaChatVoice.vue";
import TopPanelSearchUtils, {SearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import MentionDropdown from "@/components/navBar/chat/MentionDropdown.vue";

const props = defineProps<{
  presenter: ChatPresenter;
}>();

const messageText = props.presenter.messageTextComputed();
const activeEmojiTab = ref('smileys');
const attachments = ref<ChatAttachment[]>([]);
const uploaderRef: Ref<QUploader | undefined> = ref();
const uploading = ref(false);
const voiceRef = ref<InstanceType<typeof NamaChatVoice>>();
const editableRef = ref<HTMLDivElement>();

const groupMembers = computed(() => props.presenter.groupMembers());

const memberResults = computed<SearchItem[]>(() => {
  if (props.presenter.mentionPhase !== 'member' || groupMembers.value.length === 0) return [];
  const query = props.presenter.mentionQuery.value;
  return ChatUtils.filterUsers(groupMembers.value, query).map((u: any) => ({
    id: u.id, type: 'user', text: Translator.translateRef(u) || u.name1 || u.name2 || u.code || '',
    name1: u.name1, name2: u.name2, code: u.code, actualCode: u.actualCode,
  } as SearchItem));
});

const entityResults = computed(() => {
  if (!props.presenter.mentionActive.value || props.presenter.mentionEntityId.value) return [];
  return TopPanelSearchUtils.searchEntitiesOnly(props.presenter.mentionQuery.value);
});

const recordResults = ref<SearchItem[]>([]);
const recordSearching = ref(false);
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastSearchId = 0;

watch([() => props.presenter.mentionQuery.value, () => props.presenter.mentionEntityId.value], ([query, entityId]) => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  if (!entityId || !query) {
    recordResults.value = [];
    recordSearching.value = false;
    return;
  }
  recordSearching.value = true;
  const searchId = ++lastSearchId;
  searchDebounceTimer = setTimeout(async () => {
    const results = await TopPanelSearchUtils.searchFor(query, entityId);
    if (searchId === lastSearchId) {
      recordResults.value = results;
      recordSearching.value = false;
    }
  }, 250);
});

const effectivePhase = computed(() => {
  const phase = props.presenter.mentionPhase;
  if (phase === 'member' && groupMembers.value.length === 0) return 'entity';
  return phase;
});
const filteredDocs = computed(() => {
  if (effectivePhase.value === 'member')
    return [...memberResults.value, ...entityResults.value];
  if (effectivePhase.value === 'record')
    return recordResults.value;
  return entityResults.value;
});
const mentionSearching = computed(() => !!props.presenter.mentionEntityId.value && recordSearching.value);
const showMentionPanel = computed(() => {
  if (!props.presenter.mentionActive.value) return false;
  if (effectivePhase.value === 'member')
    return true;
  if (effectivePhase.value === 'entity')
    return true;
  return mentionSearching.value || filteredDocs.value.length > 0;
});
const hasContent = computed(() => {
  return !!(messageText.value?.trim()) || attachments.value.length > 0;
});

watch(() => props.presenter.mentionActive.value, (active) => {
  if (!active) {
    recordResults.value = [];
    recordSearching.value = false;
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  }
});

function onAttachmentClick() {
  uploaderRef.value?.pickFiles(new Event('click'));
}

function onUploaded(info: { files: readonly any[]; xhr: any }) {
  const file = info.files[0];
  const xhr = info.xhr;
  if (!xhr)
    return;
  const fileId = JSON.parse(xhr.response).fileId;
  attachments.value.push({
    fileId: fileId,
    fileName: file.name,
    fileSize: file.size
  });
}

function onFinishUpload() {
  uploading.value = false;
  uploaderRef.value?.reset();
}

function onRejected(rejectedEntries: { failedPropValidation: string; file: File }[]) {
  rejectedEntries.forEach(e => console.error('Upload rejected:', e.failedPropValidation));
}

function removeAttachment(index: number) {
  attachments.value.splice(index, 1);
}

function onInput(e?: Event) {
  if (e?.isTrusted) props.presenter.resetEscaped();
  const el = editableRef.value;
  if (!el) return;
  const plainText = ChatUtils.getPlainText(el);
  props.presenter.onPartialUserInput(plainText);
  props.presenter.checkMention(el);
}

function onCursorMove() {
  const el = editableRef.value;
  if (!el) return;
  props.presenter.checkMention(el);
}

function onEnterKeyDown(e: KeyboardEvent) {
  e.preventDefault();
  sendAll();
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.presenter.mentionActive.value) {
    e.preventDefault();
    closeMention();
    return;
  }
  // ArrowDown re-trigger: if cursor is in a mention context but panel was dismissed, re-activate
  if (e.key === 'ArrowDown' && !props.presenter.mentionActive.value) {
    const el = editableRef.value;
    if (el) {
      props.presenter.checkMention(el);
      if (props.presenter.mentionActive.value) {
        e.preventDefault();
        return;
      }
    }
  }
}

function closeMention() {
  const el = editableRef.value;
  if (el)
    props.presenter.cancelMention(el);
}

let blurTimer: ReturnType<typeof setTimeout> | null = null;

function onInputBlur() {
  blurTimer = setTimeout(() => {
    if (props.presenter.mentionActive.value)
      closeMention();
  }, 150);
}

function onInputFocus() {
  if (blurTimer) {
    clearTimeout(blurTimer);
    blurTimer = null;
  }
}

async function sendAll() {
  const el = editableRef.value;
  if (!el) return;

  const content = ChatUtils.extractMessageContent(el);

  if (attachments.value.length > 0) {
    props.presenter.onPartialUserInput(content);
    await props.presenter.sendMessageWithAttachments(attachments.value);
    attachments.value = [];
  } else {
    await props.presenter.sendMessage(content);
  }
  el.innerHTML = '';
}

function selectMention(item: SearchItem) {
  const el = editableRef.value;
  if (!el) return;
  if (item.type === 'user') {
    props.presenter.insertUserMention(el, item);
  } else if (props.presenter.mentionPhase === 'record') {
    props.presenter.insertMention(el, item);
  } else {
    props.presenter.insertEntityPrefix(el, item);
  }
}

function onEmojiSelect(emoji: string) {
  const el = editableRef.value;
  if (!el)
    return;
  el.focus();
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(emoji);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  } else {
    el.append(document.createTextNode(emoji));
  }
  onInput();
}

// Sync external changes (e.g. draft restore) into the contenteditable
watch(messageText, (newVal) => {
  const el = editableRef.value;
  if (!el) return;
  const currentPlain = ChatUtils.getPlainText(el);
  if (currentPlain !== (newVal ?? '')) {
    if (newVal) {
      el.textContent = newVal;
    } else {
      el.innerHTML = '';
    }
  }
});

</script>

<template>
  <div class="tw-w-full tw-flex tw-flex-col tw-gap-1 tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-rounded-2xl">
    <div v-if="attachments.length > 0" class="tw-flex tw-flex-wrap tw-gap-1 tw-px-2 tw-py-1.5">
      <q-chip v-for="(attachment, index) in attachments" :key="attachment.fileId"
              removable @remove="removeAttachment(index)"
              square dense
              color="primary" text-color="white"
              class="tw-max-w-48 tw-p-2 tw-h-7 tw-rounded-lg">
        <q-icon :name="ChatUtils.getFileIcon(attachment.fileName ?? '')" color="white" size="xs" class="tw-mr-1"/>
        <span class="tw-truncate" :title="attachment.fileName" v-text="attachment.fileName"/>
        <span class="tw-text-xs tw-opacity-80 tw-ml-1" v-text="ChatUtils.formatFileSize(attachment.fileSize)"/>
      </q-chip>
    </div>
    <q-spinner-dots v-if="uploading || voiceRef?.recordingSending" size="1.5rem" color="primary"/>
    <NamaChatVoice ref="voiceRef" :presenter="presenter"/>
    <MentionDropdown v-if="showMentionPanel" :items="filteredDocs" :phase="effectivePhase ?? 'member'"
                     :searching="mentionSearching"
                     @select="selectMention"
                     @close="closeMention"/>
    <div v-if="!voiceRef?.isRecording"
         class="nama-chat-editable-wrapper">
      <div class="tw-flex tw-items-center tw-shrink-0">
        <q-btn round dense flat icon="add">
          <q-menu anchor="bottom middle" self="top middle" transition-show="jump-up" transition-hide="jump-down"
                  class="tw-rounded-lg !tw-bg-namaBackground-bright-light dark:!tw-bg-namaBackground-bright-dark">
            <q-list style="min-width: 100px">
              <q-item clickable v-close-popup @click="onAttachmentClick">
                <q-item-section>
                  <NamaLabel text="attachment">
                    <NamaIcon defaultIcon="attachment" class="active-primary"/>
                  </NamaLabel>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
        <q-btn round dense flat class="tw-text-sm" icon="sentiment_satisfied_alt">
          <q-menu anchor="top middle" self="bottom middle" transition-show="jump-up" transition-hide="jump-down" class="tw-rounded-lg">
            <div class="tw-w-72 tw-h-80 tw-flex tw-flex-col tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark ">
              <q-tabs v-model="activeEmojiTab" dense inline-label class="tw-shrink-0" active-color="primary" indicator-color="primary">
                <q-tab v-for="(Category, key) in ChatUtils.EMOJI_CATEGORIES" :key="key" :name="key" class="tw-min-w-0 tw-px-1">
                  <span class="tw-text-lg" v-text="Category.icon"/>
                </q-tab>
              </q-tabs>
              <q-separator/>
              <q-tab-panels v-model="activeEmojiTab"
                            class="tw-grow tw-overflow-hidden tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark">
                <q-tab-panel v-for="(Category, key) in ChatUtils.EMOJI_CATEGORIES" :key="key" :name="key" class="tw-p-1 tw-h-full tw-overflow-y-auto">
                  <div class="tw-flex tw-flex-wrap tw-gap-0.5">
                    <q-btn v-for="emoji in Category.emojis" :key="emoji" flat dense round size="1.5em"
                           class="tw-justify-center tw-cursor-pointer hover:tw-bg-gray-200 hover:tw-bg-opacity-35 dark:hover:tw-bg-gray-600 dark:hover:tw-bg-opacity-35 tw-transition-colors"
                           @click="onEmojiSelect(emoji)" v-text="emoji"/>
                  </div>
                </q-tab-panel>
              </q-tab-panels>
            </div>
          </q-menu>
        </q-btn>
      </div>
      <div ref="editableRef"
           contenteditable="true"
           class="nama-chat-editable"
           @input="onInput"
           @keydown.enter="onEnterKeyDown"
           @keydown="onKeyDown"
           @keyup="onCursorMove"
           @click="onCursorMove"
           @blur="onInputBlur"
           @focus="onInputFocus"
           :data-placeholder="Translator.translateId('typeAMessage')"
      ></div>

      <div class="tw-flex tw-items-center tw-shrink-0">
        <q-btn v-if="hasContent" round dense flat
               @click="sendAll" :title="Translator.translateId('send')" class="tw-h-6">
          <NamaIcon defaultIcon="nm-chatSend" class="tw-size-5" :class="[Translator.isLanguageRTL()?'tw-rotate-180':'']"/>
        </q-btn>
        <q-btn v-else round dense flat @click="voiceRef?.startRecording()" :title="Translator.translateId('voiceMessage')" class="tw-h-6">
          <NamaIcon defaultIcon="mic_none" class="tw-size-5"/>
        </q-btn>
      </div>
    </div>

    <!-- Hidden q-uploader: auto-uploads to server, same as FileUploaderWidget -->
    <q-uploader ref="uploaderRef" auto-upload multiple flat dense hide-bottom-space no-thumbnails
                :url="FileUploaderWidgetUtil.getUrl"
                @start="uploading=true"
                @finish="onFinishUpload"
                @uploaded="onUploaded" @rejected="onRejected" class="tw-hidden"/>
  </div>
</template>

<style scoped>
.nama-chat-editable-wrapper {
  @apply tw-flex tw-items-center tw-rounded-2xl tw-border tw-border-[#c2c2c2] tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark dark:tw-border-gray-400 tw-px-1;
}

.nama-chat-editable {
  @apply tw-flex-1 tw-outline-none tw-px-2 tw-py-2 tw-text-base dark:tw-text-namaFontColor-dark tw-max-h-32 tw-overflow-y-auto;
  min-height: 2.25rem;
  word-break: break-word;
}

.nama-chat-editable:empty::before {
  content: attr(data-placeholder);
  @apply tw-text-gray-400;
  pointer-events: none;
}
</style>
